import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { attachScrollTriggerOrPlayNow } from "../utils/scrollTriggerHelpers";

gsap.registerPlugin(ScrollTrigger);

/**
 * Banner block animation.
 *
 * Trigger: ScrollTrigger one-shot all'entrata in viewport (top 85%).
 *
 * Sequenza:
 *   T=0.0  bg: fade in (0.6s) + grayscale 1→0 (5s) + contrast 0.95→1.02 (5s)
 *               + zoom 1.05→1 (8s, lineare)
 *   T=0.4  titolo: fade-up + blur (1.0s, power2.out)
 *   T=0.7  testo: fade-up + blur (0.8s, power2.out)
 *   T=1.0  CTA: fade-up + blur (0.6s, power2.out)
 *
 * Hover gestito da CSS (vedi banner.scss).
 */

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const initBannerBlock = (block) => {
  const bg = block.querySelector("[data-banner-bg]");
  const bgImg = bg ? bg.querySelector("img") : null;
  const title = block.querySelector("[data-banner-title]");
  const text = block.querySelector("[data-banner-text]");
  const cta = block.querySelector("[data-banner-cta]");

  if (!bgImg && !title) return;

  // Reduced motion: niente animazione
  if (prefersReducedMotion()) {
    block.setAttribute("data-banner-ready", "true");
    return;
  }

  // Stato iniziale GSAP (override CSS per sicurezza)
  if (bgImg) {
    gsap.set(bgImg, { opacity: 0, scale: 1.05, transformOrigin: "center center" });
  }
  if (bg) {
    gsap.set(bg, { "--banner-grayscale": 1, "--banner-contrast": 0.95 });
  }
  [title, text, cta].forEach((el) => {
    if (el) gsap.set(el, { opacity: 0, y: 20, filter: "blur(6px)" });
  });

  // Timeline master (paused, attiva via helper)
  const tl = gsap.timeline({ paused: true });

  // Bg: fade in
  if (bgImg) {
    tl.to(bgImg, {
      opacity: 1,
      duration: 0.6,
      ease: "power2.out",
    }, 0);

    // Zoom out lento (8s lineare, continua sotto la sequenza testi)
    tl.to(bgImg, {
      scale: 1,
      duration: 8,
      ease: "none",
    }, 0);
  }

  // Grayscale + contrast: ritorna a colori in 5s
  if (bg) {
    tl.to(bg, {
      "--banner-grayscale": 0,
      "--banner-contrast": 1.02,
      duration: 5,
      ease: "power2.inOut",
    }, 0);
  }

  // Titolo
  if (title) {
    tl.to(title, {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      duration: 1.0,
      ease: "power2.out",
    }, 0.4);
  }

  // Testo
  if (text) {
    tl.to(text, {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      duration: 0.8,
      ease: "power2.out",
    }, 0.7);
  }

  // CTA
  if (cta) {
    tl.to(cta, {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      duration: 0.6,
      ease: "power2.out",
    }, 1.0);
  }

  attachScrollTriggerOrPlayNow(tl, {
    trigger: block,
    onComplete: () => block.setAttribute("data-banner-ready", "true"),
  });
};

const initAllBanner = () => {
  const blocks = document.querySelectorAll("[data-banner-block]");
  blocks.forEach(initBannerBlock);
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllBanner);
  } else {
    initAllBanner();
  }
};
